import React, { useState } from 'react';
import {
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Paper,
  alpha,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { toggleTheme } from '../../store/actions/themeActions';
import {
  TabPanel,
  PreferenceToggle,
  ThemeVariantSelector,
  ScrollToTopButton,
  ErrorBoundary
} from './components';
import {
  WelcomeMessage,
  ReferralBanner,
  ProfileHeader
} from './sections';
import {
  useProfileData,
  useSettings,
  useModals
} from './hooks';
import AddBankModal from '../PayIn/AddBankModal';
import SettingsDialog from './Settings/SettingsDialog';
import PreferencesTab from './Settings/PreferencesTab';
import ExchangeMessageSlider from '../Common/ExchangeMessageSlider';
import AccountDetailsTab from './AccountDetailsTab';
import PageTabs from '../Common/PageTabs';
import { TAB_LABELS, TAB_INDICES, SNACKBAR_DURATION } from './constants/profileConstants';

const Profile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const themeMode = useSelector(state => state.theme.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    userData,
    userSettings,
    isUpdating,
    brokerageMessage,
    showWelcome,
    setShowWelcome,
    referralCopied,
    setReferralCopied,
  } = useProfileData();

  const {
    openSettingsDialog,
    setOpenSettingsDialog,
    orderType,
    setOrderType,
    productType,
    setProductType,
    defaultQtyByExchange,
    setDefaultQtyByExchange,
    settingsUpdated,
    setSettingsUpdated,
    updateError,
    handleSaveSettings,
  } = useSettings();

  const {
    isAddBankModalOpen,
    setIsAddBankModalOpen,
    handleAddBankAccount,
  } = useModals();

  const [tabValue, setTabValue] = useState(TAB_INDICES.ACCOUNT_DETAILS);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  if (!userData) {
    return null;
  }

  return (
    <Box sx={{
      width: '100%',
      backgroundColor: theme.palette.background.default,
      height: '100%',
      overflow: 'auto',
      position: 'relative',
    }}>
      <WelcomeMessage
        showWelcome={showWelcome}
        onClose={() => setShowWelcome(false)}
        userData={userData}
      />

      {/* Referral URL Banner */}
      {userData?.referral_url && (
        <ReferralBanner
          userData={userData}
          onCopySuccess={() => setReferralCopied(true)}
        />
      )}

      {/* <ProfileHeader userData={userData} /> */}

      {/* Exchange Messages Slider Section */}
      <Box sx={{
        width: '100%',
        px: 0,
        mb: 3,
      }}>
        <ExchangeMessageSlider
          exchanges={['MCX', 'NFO', 'CDS', 'NSE', 'ALL']}
          height="auto"
          autoplay={true}
          showPagination={true}
          showNavigation={false}
          slidesPerView={1}
          spaceBetween={10}
          effect="slide"
          brokerageMessage={brokerageMessage || {}}
        />
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', px: 0 }}>
        <Paper elevation={0} sx={{
          borderRadius: 0,
          overflow: 'hidden',
          mb: 4,
          width: '100%',
          boxShadow: {
            xs: 'none',
            sm: '0 1px 3px rgba(0,0,0,0.1)',
          },
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
        }}>
          <Box sx={{ width: '100%', mb: 2 }}>
            <PageTabs
              value={tabValue}
              onChange={handleTabChange}
              tabs={[
                { label: TAB_LABELS.ACCOUNT_DETAILS, value: TAB_INDICES.ACCOUNT_DETAILS },
                { label: TAB_LABELS.PREFERENCES, value: TAB_INDICES.PREFERENCES },
              ]}
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent',
                px: { xs: 1, sm: 2 }
              }}
            />
          </Box>

          {/* Account Details Tab */}
          <TabPanel value={tabValue} index={TAB_INDICES.ACCOUNT_DETAILS}>
            <AccountDetailsTab
              userData={userData}
              onAddBankAccount={handleAddBankAccount}
            />
          </TabPanel>

          {/* Preferences Tab */}
          <TabPanel value={tabValue} index={TAB_INDICES.PREFERENCES}>
            <Container maxWidth={false} disableGutters>
              <PreferencesTab
                userSettings={userSettings || {}}
                handleOpenSettingsDialog={() => setOpenSettingsDialog(true)}
              />

              {/* Theme Preference */}
              <ThemeVariantSelector />
            </Container>
          </TabPanel>
        </Paper>
      </Box>

      {/* Settings Dialog */}
      <SettingsDialog
        open={openSettingsDialog}
        onClose={() => setOpenSettingsDialog(false)}
        orderType={orderType}
        setOrderType={setOrderType}
        productType={productType}
        setProductType={setProductType}
        defaultQtyByExchange={defaultQtyByExchange}
        setDefaultQtyByExchange={setDefaultQtyByExchange}
        onSave={handleSaveSettings}
        isUpdating={isUpdating}
        updateError={updateError}
        theme={theme}
      />

      {/* Settings Updated Snackbar */}
      <Snackbar
        open={settingsUpdated}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => setSettingsUpdated(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSettingsUpdated(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Settings updated successfully
        </Alert>
      </Snackbar>

      {/* Referral URL Copied Snackbar */}
      <Snackbar
        open={referralCopied}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => setReferralCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setReferralCopied(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Referral URL copied to clipboard!
        </Alert>
      </Snackbar>

      {/* AddBankModal */}
      <AddBankModal
        open={isAddBankModalOpen}
        onClose={() => setIsAddBankModalOpen(false)}
        onSubmit={(data) => {
          // Handle the bank account submission here
          setIsAddBankModalOpen(false);
        }}
      />

      <ScrollToTopButton />
    </Box>
  );
};

export default Profile;